import * as trace from 'owa-trace';
import { createOwsError, OwsError } from './owsError';
import { runWithQoS, OwsCallDetails } from '../../framework/logging/runWithQoS';
import type { TraceErrorObject } from 'owa-trace';
import {
    isUnsuccessfulResponse,
    getUnsuccessfulResponseMessage,
    getUnsuccessfulResponseCode,
} from './owsResponseUtils';
import type { Feature } from '../../framework/logging/logFeature';
import { createRequestQueue } from 'owa-service-utils';
import type { CustomData } from 'owa-analytics-types';

const requestQueue = createRequestQueue(10);

function getJson<T>(responseOrJson: T | Response): Promise<T> {
    return responseOrJson instanceof Response
        ? responseOrJson.clone().json()
        : Promise.resolve(responseOrJson as T);
}

export interface RunOwsRequestWithMonitorConfig<T, R> {
    scenarioName: string;
    feature: Feature;
    operation: () => Promise<T>;
    transformResponse: (response: T) => R;
    qoSConfig?: QoSConfig;
}

export interface QoSConfig {
    isExpectedFailure?: (callDetails: OwsCallDetails) => boolean;
    additionalQoSLogProperty?: CustomData;
}

export function runOwsRequestWithMonitor<T, R>(
    config: RunOwsRequestWithMonitorConfig<T, R>
): Promise<R> {
    return requestQueue.add(() => {
        return runWithQoS({
            scenarioName: config.scenarioName,
            feature: config.feature,
            operation: new Promise<R>(
                (
                    resolve: (value?: R | PromiseLike<R>) => void,
                    reject: (error: OwsError) => void
                ) => {
                    config
                        .operation()
                        .then(
                            processSuccessResponse(
                                config.scenarioName,
                                resolve,
                                reject,
                                config.transformResponse
                            ),
                            (error: TraceErrorObject) => {
                                reject(
                                    createOwsError({
                                        name: error.name,
                                        message: error.message,
                                        serviceFetchError: error,
                                    })
                                );
                            }
                        );
                }
            ),
            additionalQoSLogProperty: getQoSData(requestQueue.getRunningCount(), config.qoSConfig),
            isExpectedFailure: config.qoSConfig?.isExpectedFailure,
        });
    });
}

function getQoSData(requestCounter: number, qoSConfig: QoSConfig): CustomData {
    if (!qoSConfig || !qoSConfig.additionalQoSLogProperty) {
        return { parallelRequests: requestCounter };
    }

    return { ...qoSConfig.additionalQoSLogProperty, parallelRequests: requestCounter };
}

const processSuccessResponse = <R, T>(
    scenarioName: string,
    resolve: (value?: R | PromiseLike<R>) => void,
    reject: (error: OwsError) => void,
    transformResponse: (response: T) => R
) => (responseOrJson: T | Response) => {
    getJson(responseOrJson).then((json: T) => {
        if (isUnsuccessfulResponse(json)) {
            const response =
                responseOrJson instanceof Response ? (responseOrJson as Response) : null;

            if (!response) {
                trace.errorThatWillCauseAlert(
                    `runOwsRequestWithMonitor: No response for: "${scenarioName}". Use FullResponse operation.`
                );
            }

            reject(
                createOwsError({
                    message: getUnsuccessfulResponseMessage(json),
                    name: scenarioName + '_UnsuccessfulResponse',
                    response: response,
                    responseCode: getUnsuccessfulResponseCode(json),
                })
            );
        } else {
            try {
                const transformedResponse = transformResponse(json);
                resolve(transformedResponse);
            } catch (error) {
                reject(
                    createOwsError({
                        message: error.message,
                        name: error.name,
                        isTransformError: true,
                    })
                );
                trace.errorThatWillCauseAlert(
                    `runOwsRequestWithMonitor: Unhandled transform error for scenario: "${scenarioName}".
                    isValueToTransformNullOrUndefined: ${responseOrJson == null}`, // double (and not triple) equals is intentional here
                    error
                );
            }
        }
    });
};
